import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { Observable, Subscription, tap } from 'rxjs';
import { ROUTE_PATHS } from '../../../../../shared/constants/route-paths';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { EditComponentType } from '../../../../../shared/enums/edit-component-type';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { OnUnsavedChanges } from '../../../../../shared/internal/on-unsaved-changes';
import { prefixWithSlash } from '../../../../../shared/internal/prefix-with-slash';
import { SidebarConfiguration } from '../../../../../shared/internal/sidebar/sidebar-configuration';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { ErrorMessageQuery } from '../../../../../shared/models/error-messages/error-message-query';
import { DetailsViewConfiguration } from '../../../../../shared/models/internal/details/details-view-configuration';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { ErrorMessageApiService } from '../../../../services/api/error-message-api.service';
import { PreselectionService } from '../../../../services/preselection.service';
import { TranslationService } from '../../../../services/translation.service';
import { CrudOverviewBaseComponent } from '../../../base/crud-overview-base.component';
import { SidebarComponent } from '../../../common/sidebar/sidebar.component';
import { AddComponent } from '../../../icons/add/add.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { ErrorMessageTableComponent } from '../../../tables/error-message/error-message-table/error-message-table.component';
import { CurrentLanguageAndSwPartNumberView } from '../../../sw-part-number/current-language-and-sw-part-number-view/current-language-and-sw-part-number-view';
import { Pagination } from '../../../../../shared/models/internal/pagination';
import { errorToString } from '../../../../common/common-utils';

@Component({
  selector: 'app-error-message-overview',
  imports: [
    AddComponent,
    ErrorMessageTableComponent,
    RouterModule,
    CommonModule,
    SidebarComponent,
    EnabledByPolicyDirective,
    CurrentLanguageAndSwPartNumberView,
    AgentActionDirective,
  ],
  templateUrl: './error-message-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './error-message-overview.component.scss',
})
export class ErrorMessageOverviewComponent
  extends CrudOverviewBaseComponent<ErrorMessage>
  implements OnInit, OnUnsavedChanges<ErrorMessage>, OnDestroy
{
  public recyclingModeEnabled = false;
  public errorMessageDetailsConfig?: DetailsViewConfiguration;
  public errorMessageEditComponent = EditComponentType.ErrorMessage;
  public errorMessageEditComponentId?: string;
  public sidebarConfiguration: SidebarConfiguration = new SidebarConfiguration();

  private languageServiceSubscription?: Subscription;
  private currentQuery?: ErrorMessageQuery;

  private readonly router = inject(Router);
  private readonly errorMessageApi = inject(ErrorMessageApiService);
  private readonly preselectionService = inject(PreselectionService);
  private readonly translationService = inject(TranslationService);

  constructor() {
    super(STORAGE_KEYS.UNSAVED_ERROR_MESSAGE_CHANGES);
    this.languageServiceSubscription = this.translationService.languageChangeSubject.subscribe({
      next: async () => {
        if (this.currentQuery) {
          if (this.selectedTableElement.isElementSelected) {
            const requestedPage = this.selectedTableElement.currentPage || 0;
            await this.fetchErrorMessagesUntilPage(this.currentQuery, requestedPage);
            this.handleNewSelection(this.selectedTableElement.selectedElementId!);
          } else {
            await this.fetchErrorMessagesUntilPage(this.currentQuery, 0);
          }
        }
      },
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.sidebarConfiguration.linkAreaEnabled = true;
    const dtcId = this.preselectionService.getIdAndClear(PreselectionType.ErrorMessage);
    if (dtcId) {
      this.fetchErrorMessage(dtcId);
    }
  }

  public override ngOnDestroy(): void {
    if (this.languageServiceSubscription) {
      this.languageServiceSubscription.unsubscribe();
    }
  }

  public onSearchTriggered(query: ErrorMessageQuery): void {
    const blocked = this.areThereUnsavedChanges(this.getFriendlyItemName(this.selectedItem), () => {
      this.search(query);
    });

    if (!blocked) {
      this.search(query);
    }
  }

  public onSortByChanged(query: ErrorMessageQuery): void {
    this.onSearchTriggered(query);
  }

  public onSoftDeleteTriggered(errorMessage: ErrorMessage): void {
    this.errorMessageApi.setRemoveFlag(errorMessage?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(errorMessage?.id);
        this.sidebarOpen = false;
        this.toastr.success('Error message has been deactivated successfully');
      },
      error: (error) => {
        console.error('Error soft deleting error message:', errorToString(error));
      },
    });
  }

  public onHardDeleteTriggered(errorMessage: ErrorMessage): void {
    this.errorMessageApi.deleteErrorMessage(errorMessage?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(errorMessage?.id);
        this.sidebarOpen = false;
        this.toastr.success('Error message has been deleted successfully');
      },
      error: (error) => {
        console.error('Error deleting error message:', errorToString(error));
      },
    });
  }

  public onRestoreErrorMessageTriggered(errorMessage: ErrorMessage): void {
    this.errorMessageApi.restoreErrorMessage(errorMessage?.id ?? '').subscribe({
      next: () => {
        this.removeItemFromList(errorMessage?.id);
        this.sidebarOpen = false;
        this.toastr.success('Error message has been restored successfully');
      },
      error: (error) => {
        console.error('Error restoring error message:', errorToString(error));
      },
    });
  }

  public onScrolledToTheBottom(query: ErrorMessageQuery): void {
    this.fetchErrorMessages(query, false);
  }

  public onUpdatedErrorMessage(errorMessage: ErrorMessage): void {
    this.refreshItemInListAfterUpdate(errorMessage.id);
    this.selectedItem = errorMessage;
    if (errorMessage.id) {
      this.selectedTableElement.setSelectedElementId(errorMessage.id, this.paginationService.currentPageNumber);
    }
    this.clearUnsavedData();
    this.handleNewSelection(errorMessage.id ?? '');
  }

  public createErrorMessage(): void {
    this.router.navigate([prefixWithSlash(ROUTE_PATHS.errorMessageNew)]);
  }

  protected override handleNewSelection(id: string): void {
    this.sidebarOpen = true;
    const em = this.getItemById(id);
    this.sideBarTitle = `Error Message ${em?.code ?? 0} (${em?.headerOnTerminal})`;

    this.errorMessageApi.getErrorMessage(id).subscribe({
      next: (errorMessage) => {
        this.configureDetailsView(errorMessage);
        this.errorMessageEditComponentId = errorMessage.id;
        this.selectedItem = errorMessage;
      },
      error: (error) => {
        console.error('Error fetching error message:', errorToString(error));
      },
    });
  }

  protected override getFriendlyItemName(item?: ErrorMessage): string {
    if (item) {
      return (item.code ?? 0).toString();
    }
    return '';
  }

  protected override configureDetailsView(item: ErrorMessage): void {
    this.errorMessageDetailsConfig = {
      entityId: item.id,
      title: 'Error Message ' + item.code,
      shortDescription: item.swPartNumber?.displayId ?? '-',
      translationStateTerminal: item.translationStateTerminal,
      textAreas: [
        {
          label: 'Header on Terminal',
          value: item.headerOnTerminal ?? '-',
          sequence: 1,
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
        {
          label: 'Details on Terminal',
          value: item.detailsOnTerminal ?? '-',
          sequence: 2,
          isTranslatable: !this.translationService.defaultLanguageSelected(),
        },
      ],
      singleLineDetails: [
        {
          sequence: 3,
          attributes: [{ label: 'Category', value: item.category ?? '-' }],
        },
      ],
      displayLinkedDtcs: true,
      internalNotes: item.internalNotes,
    };
  }

  protected async fetchErrorMessages(query: ErrorMessageQuery, resetPagination: boolean): Promise<void> {
    this.currentQuery = query;
    await this.fetchPagedData(resetPagination, (page, pageSize) =>
      this.getRecycledOrNormalErrorMessages(query, page, pageSize)
    );
  }

  private async fetchErrorMessagesUntilPage(query: ErrorMessageQuery, targetPage: number): Promise<void> {
    this.currentQuery = query;
    await this.fetchPagedDataUntilPage(targetPage, (page, pageSize) =>
      this.getRecycledOrNormalErrorMessages(query, page, pageSize)
    );
  }

  private getRecycledOrNormalErrorMessages(
    query: ErrorMessageQuery,
    page: number,
    pageSize: number
  ): Observable<Pagination<ErrorMessage>> {
    query.page = page;
    query.pageSize = pageSize;

    if (this.recyclingModeEnabled) {
      return this.errorMessageApi.getRecycledErrorMessages(
        query,
        TranslationService.defaultLanguageCode(),
        this.translationService.getLanguage()
      );
    }
    return this.errorMessageApi.getErrorMessages(
      query,
      TranslationService.defaultLanguageCode(),
      this.translationService.getLanguage()
    );
  }

  private fetchErrorMessage(id: string): void {
    const query = {
      ids: id,
    } as ErrorMessageQuery;

    this.fetchPagedData(true, (page, pageSize) => {
      query.page = page;
      query.pageSize = pageSize;
      return this.errorMessageApi
        .getErrorMessages(query, TranslationService.defaultLanguageCode(), this.translationService.getLanguage())
        .pipe(
          tap((ems) => {
            const em = ems.items.filter((em) => em.id == id)[0];
            if (!em) {
              console.error(`Couldn't find preselected ErrorMessage with id ${id}`);
              return;
            }
            this.swPartNumberService.setSwPartNumber(em.swPartNumber!);
            this.onItemClicked(id);
          })
        );
    });
  }

  private refreshItemInListAfterUpdate(id?: string): void {
    if (!id) {
      return;
    }
    const query = {
      ids: id,
    } as ErrorMessageQuery;
    this.errorMessageApi
      .getErrorMessages(query, TranslationService.defaultLanguageCode(), this.translationService.getLanguage())
      .subscribe({
        next: (errorMessages) => {
          const em = errorMessages.items.shift();
          if (em) {
            this.items = this.items?.map((existingErrorMessage) =>
              existingErrorMessage.id === em.id ? em : existingErrorMessage
            );
          }
        },
        error: (error) => {
          console.error(errorToString(error));
        },
      });
  }

  private search(query: ErrorMessageQuery): void {
    this.items = [];
    this.sidebarOpen = false;
    this.selectedItem = undefined;
    this.selectedTableElement.clearSelection();
    this.fetchErrorMessages(query, true);
  }
}
